import React, { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { Provider } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { store } from './store/store';
import { darkTheme } from './theme/darkTheme';
import WidgetDashboard from './components/dashboard/WidgetDashboard';
import InsightsPage from './components/pages/InsightsPage';
import ProductionInsightsPage from './components/pages/ProductionInsightsPage';
import ErrorBoundary from './components/ErrorBoundary';
import { DashboardConfig, WidgetType } from './types/widget';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/auth/Login';
import { analytics } from './firebase';

// Mock dashboard configuration
const createMockDashboardConfig = (userId: string): DashboardConfig => ({
  id: 'production-dashboard',
  userId: userId,
  name: 'Production Analytics Dashboard',
  widgets: [
    {
      id: 'timing-tower-1',
      type: WidgetType.TIMING_TOWER,
      title: 'Timing Tower',
      position: { x: 0, y: 0, z: 0 },
      size: { width: 4, height: 5, minWidth: 3, minHeight: 4 },
      settings: {},
      isVisible: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    {
      id: 'production-velocity-1',
      type: WidgetType.PRODUCTION_VELOCITY,
      title: 'Production Velocity',
      position: { x: 4, y: 0, z: 1 },
      size: { width: 8, height: 5, minWidth: 6, minHeight: 4 },
      settings: {},
      isVisible: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    {
      id: 'document-library-1',
      type: WidgetType.DOCUMENT_LIBRARY,
      title: 'Document Library',
      position: { x: 0, y: 5, z: 2 },
      size: { width: 4, height: 5, minWidth: 3, minHeight: 5 },
      settings: {},
      isVisible: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    {
      id: 'drift-analysis-1',
      type: WidgetType.DRIFT_ANALYSIS,
      title: 'Production Drift Analysis',
      position: { x: 4, y: 5, z: 3 },
      size: { width: 8, height: 5, minWidth: 3, minHeight: 3 },
      settings: {},
      isVisible: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
  ],
  layout: {
    columns: 12,
    rows: 8,
    gap: 16,
    padding: 16,
  },
  theme: {
    mode: 'dark',
    primaryColor: '#4299E1',
    backgroundColor: '#1A202C',
    cardBackgroundColor: '#2D3748',
    textColor: '#FFFFFF',
    accentColor: '#4299E1',
  },
  createdAt: new Date(),
  updatedAt: new Date(),
});

const DashboardRoute: React.FC = () => {
  const { user } = useAuth();
  
  const handleSave = (config: DashboardConfig) => {
    console.log('Saving dashboard configuration:', config);
  };

  const handleExport = () => {
    console.log('Exporting dashboard configuration');
  };

  const handleImport = (config: DashboardConfig) => {
    console.log('Importing dashboard configuration:', config);
  };

  return (
    <WidgetDashboard
      dashboardConfig={createMockDashboardConfig(user?.uid || 'anonymous')}
      onSave={handleSave}
      onExport={handleExport}
      onImport={handleImport}
    />
  );
};

const ProtectedRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <Routes>
      <Route path="/" element={<DashboardRoute />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/production-insights" element={<ProductionInsightsPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  // Initialize Firebase Analytics on app load
  useEffect(() => {
    if (analytics) {
      console.log('Firebase Analytics initialized');
    }
  }, []);

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider theme={darkTheme}>
          <CssBaseline />
          <BrowserRouter>
            <AuthProvider>
              <ProtectedRoutes />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
